import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";

const STATUS_LABEL = {
  draft: { label: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  pending_approval: {
    label: "Pending",
    cls: "bg-amber-100 text-amber-900 border-amber-200",
  },
  approved: {
    label: "Approved",
    cls: "bg-emerald-100 text-emerald-900 border-emerald-200",
  },
  rejected: {
    label: "Rejected",
    cls: "bg-red-100 text-red-900 border-red-200",
  },
  changes_requested: {
    label: "Changes Requested",
    cls: "bg-orange-100 text-orange-900 border-orange-200",
  },
  sent: {
    label: "Sent",
    cls: "bg-sky-100 text-sky-900 border-sky-200",
  },
  paid: {
    label: "Paid",
    cls: "bg-emerald-200 text-emerald-900 border-emerald-300",
  },
  cancelled: {
    label: "Cancelled",
    cls: "bg-zinc-200 text-zinc-700 border-zinc-300",
  },
};

const PAYMENT_LABEL = {
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  partial: { label: "Partial", cls: "bg-amber-100 text-amber-900 border-amber-200" },
  pending: { label: "Pending", cls: "bg-red-100 text-red-900 border-red-200" },
};

export default function Invoices() {
  const { user, company } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage] = useState(20);

  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [status, setStatus] = useState("all");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [showFilters, setShowFilters] = useState(false);

  const symbol = company?.currency_symbol || "₹";

  const canCreate = user.role === "admin" || user.role === "manager";

  const activeFilterCount =
    (status !== "all" ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [status, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);

    try {
      const params = {
        page,
        per_page: perPage,
      };

      if (status !== "all") params.status = status;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await api.get("/invoices", { params });

      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, status, debouncedSearch, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
            Billing
          </div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">
            Invoices
          </h1>
        </div>

        {canCreate && (
          <Link to="/app/invoices/new">
            <Button
              className="bg-[#1A1A1A] text-white hover:bg-black rounded-full"
              data-testid="invoices-create-btn"
            >
              <Plus size={16} className="mr-1" /> New Invoice
            </Button>
          </Link>
        )}
      </div>

      {/* Search + filter toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:w-80">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            placeholder="Search invoice or client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 bg-white"
            data-testid="invoices-search-input"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters((v) => !v)}
        >
          <SlidersHorizontal size={14} className="mr-1" /> Filters
          {activeFilterCount > 0 && (
            <Badge
              variant="outline"
              className="ml-2 h-5 min-w-5 px-1 rounded-full border-black/10 bg-[#1A1A1A] text-white text-[10px] flex items-center justify-center"
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-3">
            <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  className="w-44 bg-white"
                  data-testid="invoices-status-filter"
                >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="unpaid">Pending Payment</SelectItem>
                <SelectItem value="partial">Partial Payment</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-44 bg-white"
            />

            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-44 bg-white"
            />

            <Button variant="outline" size="sm" onClick={clearFilters}>
              <RotateCcw size={14} className="mr-1" /> Reset
            </Button>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-black/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-sm text-zinc-500 py-10"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-sm text-zinc-500 py-10"
                >
                  No invoices yet.
                  {canCreate && (
                    <>
                      {" "}
                      <Link
                        to="/app/invoices/new"
                        className="text-[#D85C38] hover:underline"
                      >
                        Create your first invoice
                      </Link>
                      .
                    </>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              items.map((invoice) => {
                const statusInfo =
                  STATUS_LABEL[invoice.status] || STATUS_LABEL.draft;

                return (
                  <TableRow
                    key={invoice.id}
                    className="cursor-pointer hover:bg-black/[0.02]"
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <TableCell className="font-medium">
                      <Link to={`/app/invoices/${invoice.id}`}>
                        {invoice.invoice_number}
                      </Link>
                    </TableCell>

                    <TableCell className="text-zinc-600">
                      {invoice.client_name || invoice.client?.name || "—"}
                    </TableCell>

                    <TableCell className="text-zinc-600">
                      {invoice.event_name || invoice.event?.name || "—"}
                    </TableCell>

                    <TableCell className="text-sm text-zinc-600">
                      {invoice.invoice_date
                        ? new Date(invoice.invoice_date).toLocaleDateString(
                            "en-IN"
                          )
                        : "—"}
                    </TableCell>

                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`capitalize text-xs font-medium border ${statusInfo.cls}`}
                      >
                        {statusInfo.label}
                      </Badge>
                    </TableCell>

<TableCell>
  {invoice.payment_status ? (
    <Badge
      variant="outline"
      className={`capitalize text-xs font-medium border ${
        (PAYMENT_LABEL[invoice.payment_status] || {}).cls || "bg-zinc-100 text-zinc-700 border-zinc-200"
      }`}
    >
      {(PAYMENT_LABEL[invoice.payment_status] || {}).label || invoice.payment_status}
    </Badge>
  ) : (
    <span className="text-sm text-zinc-500">—</span>
  )}
</TableCell>

                    <TableCell className="text-right font-medium">
                      {symbol}
                      {invoice.total?.toFixed(2) || "0.00"}
                    </TableCell>

                    <TableCell className="text-right text-emerald-600 font-medium">
                      {symbol}
                      {invoice.amount_paid?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="rounded-2xl border border-black/10 bg-white p-6 text-center text-sm text-zinc-500">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-black/10 bg-white p-6 text-center text-sm text-zinc-500">
            No invoices yet.
          </div>
        ) : (
          items.map((invoice) => {
            const statusInfo =
              STATUS_LABEL[invoice.status] || STATUS_LABEL.draft;

            return (
              <Link
                key={invoice.id}
                to={`/app/invoices/${invoice.id}`}
                className="block rounded-2xl border border-black/10 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">
                      {invoice.invoice_number} ({invoice.client_name || invoice.client?.name || "—"})
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`capitalize text-xs font-medium border ${statusInfo.cls}`}
                  >
                    {statusInfo.label}
                  </Badge>
                </div>

                <div className="mt-3 flex items-center justify-between text-sm text-zinc-600">
                  <span>{invoice.booking_event_type_name || invoice.event?.name || "—"}</span>
                  <span>
                    {invoice.invoice_date
                      ? new Date(invoice.invoice_date).toLocaleDateString(
                          "en-IN"
                        )
                      : "—"}
                  </span>
                </div>

                <div className="mt-3 pt-3 border-t border-black/5 flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-zinc-500">
                  Payment:
                  {invoice.payment_status ? (
                    <Badge
                      variant="outline"
                      className={`capitalize text-[10px] font-medium border ${
                        (PAYMENT_LABEL[invoice.payment_status] || {}).cls || "bg-zinc-100 text-zinc-700 border-zinc-200"
                      }`}
                    >
                      {(PAYMENT_LABEL[invoice.payment_status] || {}).label || invoice.payment_status}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </span>
                  <span className="font-medium">
                    {symbol}
                    {invoice.total?.toFixed(2) || "0.00"}{" "}
                    <span className="text-emerald-600 font-medium">
                      ({symbol}
                      {invoice.amount_paid?.toFixed(2) || "0.00"} paid)
                    </span>
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">
          Showing {start} - {end} of {total}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft size={16} />
          </Button>

          <span className="text-sm font-medium">
            Page {page} of {pages}
          </span>

          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}